import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X, ChevronRight, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AmberAlertBanner() {
  const [alerts, setAlerts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const response = await fetch(`${API}/amber-alerts/active`);
        if (response.ok) {
          const data = await response.json();
          setAlerts(data.alerts || []);
        }
      } catch (error) {
        console.error('Error fetching AMBER Alerts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAlerts();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchAlerts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Rotate through alerts if there are multiple
  useEffect(() => {
    if (alerts.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % alerts.length);
      }, 8000);
      return () => clearInterval(interval);
    }
  }, [alerts.length]);

  if (loading || alerts.length === 0 || dismissed) {
    return null;
  }

  const currentAlert = alerts[currentIndex];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        className="relative bg-gradient-to-r from-red-600 via-red-500 to-red-600 text-white overflow-hidden"
      >
        {/* Animated background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)'
          }} />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="py-3 flex items-center justify-between gap-4">
            {/* Left: Alert icon and label */}
            <div className="flex items-center gap-3 shrink-0">
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
                className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center"
              >
                <AlertTriangle className="w-5 h-5" />
              </motion.div>
              <div className="hidden sm:block">
                <div className="text-xs font-bold uppercase tracking-wider text-red-100">
                  AMBER Alert
                </div>
                <div className="text-sm font-semibold">
                  Child Abduction Emergency
                </div>
              </div>
            </div>

            {/* Center: Alert details */}
            <div className="flex-1 min-w-0">
              <Link 
                to={`/amber-alerts`}
                className="flex items-center gap-3 hover:opacity-90 transition-opacity"
              >
                {currentAlert.photo_url && (
                  <img 
                    src={currentAlert.photo_url} 
                    alt={currentAlert.name}
                    className="w-10 h-10 rounded-full object-cover border-2 border-white/30 hidden sm:block"
                  />
                )}
                <div className="min-w-0">
                  <div className="font-bold truncate">
                    {currentAlert.name}
                    {currentAlert.age && <span className="font-normal ml-2">Age {currentAlert.age}</span>}
                  </div>
                  <div className="text-sm text-red-100 truncate">
                    {currentAlert.last_seen_location || currentAlert.state || 'Location unknown'}
                    {alerts.length > 1 && (
                      <span className="ml-2 opacity-75">
                        ({currentIndex + 1} of {alerts.length} active alerts)
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2 shrink-0">
              <Button
                asChild
                size="sm"
                variant="secondary"
                className="hidden md:flex bg-white text-red-600 hover:bg-red-50 gap-1"
              >
                <Link to="/amber-alerts">
                  View Details
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </Button>
              
              <a 
                href="tel:1-800-843-5678"
                className="hidden lg:flex items-center gap-1 px-3 py-1.5 bg-white/20 rounded-md text-sm font-medium hover:bg-white/30 transition-colors"
              >
                <Phone className="w-4 h-4" />
                1-800-THE-LOST
              </a>

              <button
                onClick={() => setDismissed(true)}
                className="p-1.5 hover:bg-white/20 rounded-md transition-colors"
                aria-label="Dismiss alert"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Progress bar for rotation */}
        {alerts.length > 1 && (
          <div className="h-1 bg-red-700/50">
            <motion.div
              key={currentIndex}
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: 8, ease: 'linear' }}
              className="h-full bg-white/50"
            />
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

export default AmberAlertBanner;
